import { IconButton, InputLabel, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowDropDown, CloseOutlined } from '@mui/icons-material';
import { Modal, ScrollableView, StickyStackItem, groupBy as lodashGroupBy } from '@uplift/ui-v2';
import { useField } from 'formik';
import { Fragment, useEffect, useRef, useState } from 'react';

export type FullScreenSelectProps<T> = {
  label: string;
} & SelectListProps<T>;

const getOptionGroups = <T extends object>(options: T[], groupBy: (option: T) => string, groupSortOrder: FullScreenSelectProps<T>['groupSortOrder']) => {
  if (!groupBy) {
    return [ { label: '', options } ];
  }

  const groups = Object.entries(lodashGroupBy(options, groupBy)).map(([ label, options ]) => ({ label, options }));

  // will sort based on order of group label strings in this array
  if (groupSortOrder) {
    groups.sort((a, b) => {
      const indexA = groupSortOrder.indexOf(a.label);
      const indexB = groupSortOrder.indexOf(b.label);

      // If the group label is not found in the groupSortOrder array, it will be placed at the end
      const orderA = indexA === -1 ? groupSortOrder.length : indexA;
      const orderB = indexB === -1 ? groupSortOrder.length : indexB;

      return orderA - orderB;
    });
  }

  return groups;
};

export const FullScreenSelect = <T extends object>(props: FullScreenSelectProps<T>) => {
  const { classes } = useFullScreenSelectStyles();
  const [ open, setOpen ] = useState(false);
  const [ field, _meta ] = useField<string>(props.name);

  const onClose = () => setOpen(false);

  const selectedOption = props.options.find(option => props.getValue(option) === field.value);

  return (
    <>
      <div>
        <InputLabel>{props.label}</InputLabel>
        <button data-testid={`input-${props.name}`} className={classes.button} type="button" onClick={() => setOpen(true)}>
          <span>{props.getLabel(selectedOption)}</span>
          <ArrowDropDown />
        </button>
      </div>

      <Modal fullScreen open={open}>
        <div className={classes.modalTitle}>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </div>
        <ScrollableView>
          <SelectList
            onSelect={onClose}
            {...props}
          />
        </ScrollableView>
      </Modal>
    </>
  );
};

const useFullScreenSelectStyles = makeStyles({ name: 'FullScreenSelect' })(theme => ({
  modalTitle: {
    height: '44px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
  },
  button: {
    all: 'unset',
    width: '100%',
    boxSizing: 'border-box',
    height: '56px',
    cursor: 'pointer',
    border: '1px solid rgba(196, 196, 196, 1)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.common.white,
    '& svg': {
      color: theme.palette.text.secondary
    }
  },
}));


export type SelectListProps<T> = {
  options: T[];
  getLabel: (option: T) => string;
  getValue: (option: T) => string;
  getClassName?: (option: T) => string;
  name: string;
  groupBy?: (option: T) => string;
  onSelect?: () => void;
  groupSortOrder?: string[];
};

export const SelectList = <T extends object>(props: SelectListProps<T>) => {
  const isFirst = useRef(true);
  const { classes, cx } = useSelectListStyles();
  const [ field, _meta, helpers ] = useField<string>(props.name);

  const onSelectOption = (value: string) => {
    helpers.setValue(value);
    props.onSelect?.();
  };

  const optionGroups = getOptionGroups(props.options, props.groupBy, props.groupSortOrder);

  const selectedOption = props.options.find(option => props.getValue(option) === field.value);

  useEffect(() => {
    if (selectedOption && isFirst) {
      const element = document.getElementById(`list-item-${props.getValue(selectedOption)}`);

      if (element) {
        element.scrollIntoView({ block: 'center' });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <List className={classes.list}>
      {optionGroups.map((group) => (
        <Fragment key={group.label}>
          <StickyStackItem placement="top" order={0}>
            <ListItem className={cx(classes.listItem, classes.groupTitle, !group.label && classes.emptyTitle)}>
              <Typography variant="overline">{group.label}</Typography>
            </ListItem>
          </StickyStackItem>
          {group.options.map(option => {
            const value = props.getValue(option);

            return (
              <ListItemButton
                key={value}
                id={`list-item-${value}`}
                selected={value === field.value}
                onClick={() => onSelectOption(value)}
                className={cx(classes.listItem, props.getClassName?.(option))}
              >
                {props.getLabel(option)}
              </ListItemButton>
            );
          })}
        </Fragment>
      ))}
    </List>
  );
};

const useSelectListStyles = makeStyles({ name: 'SelectList' })(theme => ({
  list: {
    paddingTop: 0,
  },
  listItem: {
    padding: theme.spacing(1, 4),
    minHeight: '40px',
    '&.Mui-selected': {
      background: theme.palette.secondary.background,
      '&:hover': {
        background: theme.palette.secondary.background
      }
    }
  },
  groupTitle: {
    padding: theme.spacing(1, 2),
    background: theme.palette.background.paper,
    zIndex: 1,
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  emptyTitle: {
    minHeight: 'unset',
    padding: theme.spacing(0.5, 0)
  },
}));
